<template>
    <section v-if="enabled || error" class="authentication-devices border-light b-radius-20 p-4 mb-3">
        <div class="authentication-devices__header">
            <div>
                <strong>{{ $tl("labels.authenticationDevices.title") }}</strong>
                <p>{{ $tl("labels.authenticationDevices.description") }}</p>
            </div>
            <button v-if="devices.length > 1" type="button" class="btn btn-outline-danger btn-sm" :disabled="busy" @click="revokeAll">
                {{ $tl("labels.authenticationDevices.revokeAll") }}
            </button>
        </div>

        <div v-if="loading" class="text-muted">{{ $tl("labels.loading") }}</div>
        <div v-else-if="error" class="alert alert-danger mb-3" role="alert">{{ error }}</div>
        <p v-else-if="devices.length === 0" class="text-muted mb-3">{{ $tl("labels.authenticationDevices.empty") }}</p>
        <ul v-else class="authentication-devices__list">
            <li v-for="device in devices" :key="`${device.type}:${device.id}`" class="authentication-devices__item">
                <div>
                    <div class="authentication-devices__name">
                        {{ device.name || $tl("labels.authenticationDevices.unknownDevice") }}
                        <span class="badge text-bg-secondary">{{ deviceTypeLabel(device.type) }}</span>
                        <span v-if="device.current" class="badge text-bg-success">{{ $tl("labels.authenticationDevices.current") }}</span>
                    </div>
                    <small class="text-muted"> {{ $tl("labels.authenticationDevices.lastUsed") }} {{ formatDate(device.last_used_at) }} </small>
                </div>
                <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    :aria-label="
                        $tl('labels.authenticationDevices.revokeNamed', undefined, {
                            device: device.name || $tl('labels.authenticationDevices.unknownDevice'),
                            type: deviceTypeLabel(device.type),
                        })
                    "
                    :disabled="busy"
                    @click="revoke(device)">
                    {{ $tl("labels.authenticationDevices.revoke") }}
                </button>
            </li>
        </ul>

        <p v-if="meta.persistent && meta.persistent.enabled" class="authentication-devices__notice">
            {{
                $tl("labels.authenticationDevices.cookieNotice", undefined, {
                    cookie: meta.persistent.cookie_name,
                    idleDays: meta.persistent.idle_days,
                    absoluteMonths: meta.persistent.absolute_months,
                })
            }}
        </p>
        <p v-if="meta.mfa && meta.mfa.enabled" class="authentication-devices__notice">
            {{
                $tl("labels.authenticationDevices.mfaCookieNotice", undefined, {
                    cookie: meta.mfa.cookie_name,
                    days: meta.mfa.days,
                })
            }}
        </p>
    </section>
</template>

<script>
import axios from "axios";

export default {
    name: "AuthenticationDevices",
    data() {
        return {
            enabled: false,
            loading: true,
            busy: false,
            error: null,
            devices: [],
            meta: {},
        };
    },
    created() {
        this.fetchDevices();
    },
    methods: {
        fetchDevices() {
            this.loading = true;
            this.error = null;

            return axios
                .get("/api/authentication-devices")
                .then((response) => {
                    this.devices = response.data.data || [];
                    this.meta = response.data.meta || {};
                    this.enabled = Boolean(this.meta.enabled);
                })
                .catch(() => {
                    this.error = this.$tl("labels.authenticationDevices.error");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        revoke(device) {
            const deviceName = device.name || this.$tl("labels.authenticationDevices.unknownDevice");

            const confirmationKey =
                device.type === "mfa" ? "labels.authenticationDevices.confirmRevokeMfa" : "labels.authenticationDevices.confirmRevokePersistent";

            if (!window.confirm(this.$tl(confirmationKey, undefined, { device: deviceName }))) {
                return;
            }

            this.busy = true;
            axios
                .delete(`/api/authentication-devices/${device.type}/${device.id}`)
                .then(() => this.fetchDevices())
                .catch(() => {
                    this.error = this.$tl("labels.authenticationDevices.error");
                })
                .finally(() => {
                    this.busy = false;
                });
        },
        revokeAll() {
            if (!window.confirm(this.$tl("labels.authenticationDevices.confirmRevokeAll"))) {
                return;
            }

            this.busy = true;
            axios
                .delete("/api/authentication-devices")
                .then(() => this.fetchDevices())
                .catch(() => {
                    this.error = this.$tl("labels.authenticationDevices.error");
                })
                .finally(() => {
                    this.busy = false;
                });
        },
        formatDate(value) {
            if (!value) {
                return this.$tl("labels.authenticationDevices.unknownDate");
            }

            return new Intl.DateTimeFormat(document.documentElement.lang || "fr", {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(value));
        },
        deviceTypeLabel(type) {
            return type === "mfa" ? this.$tl("labels.authenticationDevices.mfaType") : this.$tl("labels.authenticationDevices.persistentType");
        },
    },
};
</script>

<style lang="scss" scoped>
.authentication-devices {
    margin-top: 18px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(var(--theme-main-color-rgb), 0.1) !important;
    border-radius: 16px !important;
    box-shadow: 0 18px 42px rgba(var(--theme-main-color-rgb), 0.08);
}

.authentication-devices__header,
.authentication-devices__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.authentication-devices__header {
    margin-bottom: 18px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(var(--theme-main-color-rgb), 0.08);
}

.authentication-devices__header strong {
    color: #1d2939;
    font-size: 15px;
    font-weight: 800;
}

.authentication-devices__header p {
    margin: 5px 0 0;
    color: #667085;
    font-size: 13px;
}

.authentication-devices__list {
    padding: 0;
    margin: 0;
    list-style: none;
}

.authentication-devices__item {
    padding: 14px 0;
    border-bottom: 1px solid rgba(var(--theme-main-color-rgb), 0.08);
}

.authentication-devices__name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    color: #1d2939;
    font-weight: 700;
}

.authentication-devices__notice {
    margin: 16px 0 0;
    color: #667085;
    font-size: 12px;
    line-height: 1.55;
}

@media (max-width: 575px) {
    .authentication-devices__header,
    .authentication-devices__item {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
